import { ReactNode, createContext, useContext } from 'react'

import { HTML_BLOCK_POLICY } from '../../consts/html-block-policy'
import { HtmlBlockFrameRegistry } from '../../services/html-block-frame-registry.service'
import {
  IHtmlBlockVisibility,
  createHtmlBlockVisibility,
} from '../../services/html-block-visibility.service'
import { IHtmlBlockFrameRegistry } from '../../utils/html-block/types'

export type HtmlBlockRuntime = {
  frameRegistry: IHtmlBlockFrameRegistry
  visibility?: IHtmlBlockVisibility
}

let defaultRuntime: HtmlBlockRuntime | null = null

/**
 * The frame registry and the visibility scheduler are shared by every HTML block on the
 * page: one message listener, one pair of intersection observers, one concurrency budget.
 */
const getDefaultRuntime = (): HtmlBlockRuntime => {
  if (!defaultRuntime) {
    defaultRuntime = {
      frameRegistry: new HtmlBlockFrameRegistry(window, HTML_BLOCK_POLICY),
      visibility: createHtmlBlockVisibility(window, HTML_BLOCK_POLICY),
    }
  }

  return defaultRuntime
}

const HtmlBlockRuntimeContext = createContext<HtmlBlockRuntime | null>(null)

type HtmlBlockRuntimeProviderProps = {
  runtime: HtmlBlockRuntime
  children: ReactNode
}

/** Optional override, mainly so tests can inject their own registry/visibility. */
export const HtmlBlockRuntimeProvider = ({
  runtime,
  children,
}: HtmlBlockRuntimeProviderProps) => (
  <HtmlBlockRuntimeContext.Provider value={runtime}>{children}</HtmlBlockRuntimeContext.Provider>
)

export const useHtmlBlockRuntime = (): HtmlBlockRuntime => {
  const runtime = useContext(HtmlBlockRuntimeContext)
  return runtime ?? getDefaultRuntime()
}
